import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  Eye,
  FileEdit,
  Image as ImageIcon,
  Info,
  Loader2,
  RefreshCw,
  Save,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { Textarea } from "@/components/ui/Textarea";
import { FlyerPreview } from "@/components/FlyerPreview";
import { StatusBadge } from "@/components/StatusBadge";
import { Toast, type ToastKind } from "@/components/Toast";
import { ReferenceImagesPanel } from "@/components/ReferenceImagesPanel";
import { useTournament } from "@/hooks/useTournaments";
import {
  useApproveFlyer,
  useFlyers,
  useRegenerateFlyer,
  useRunStep,
} from "@/hooks/usePipeline";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const ASPECTS: Array<{ id: "1:1" | "4:5" | "9:16"; label: string; hint: string }> = [
  { id: "1:1", label: "1:1 feed", hint: "IG feed, FB" },
  { id: "4:5", label: "4:5 portrait", hint: "IG feed verticale" },
  { id: "9:16", label: "9:16 story", hint: "IG/FB story" },
];

export default function CheckpointFlyer() {
  const { slug } = useParams<{ slug: string }>();
  const qc = useQueryClient();

  const tournament = useTournament(slug);
  const flyers = useFlyers(slug);
  const runStep = useRunStep(slug ?? "");
  const regen = useRegenerateFlyer(slug ?? "");
  const approve = useApproveFlyer(slug ?? "");

  const [feedback, setFeedback] = useState("");
  const [toast, setToast] = useState<{ kind: ToastKind; message: string } | null>(null);

  // ── Prompt editor ──────────────────────────────────────────────────
  const [promptText, setPromptText] = useState("");
  const [promptDirty, setPromptDirty] = useState(false);
  const [promptOpen, setPromptOpen] = useState(false);

  const promptQuery = useQuery({
    queryKey: ["flyer-prompt", slug],
    queryFn: () => api.getFlyerPrompt(slug ?? ""),
    enabled: Boolean(slug),
    staleTime: 60_000,
  });
  useEffect(() => {
    if (promptQuery.data && !promptDirty) {
      setPromptText(promptQuery.data.prompt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptQuery.data?.prompt]);

  const savePrompt = useMutation({
    mutationFn: () => api.saveFlyerPrompt(slug ?? "", promptText),
    onSuccess: () => {
      setPromptDirty(false);
      qc.invalidateQueries({ queryKey: ["flyer-prompt", slug] });
      setToast({
        kind: "success",
        message: `Prompt salvato (${promptText.length} char) ✓`,
      });
    },
    onError: (e) =>
      setToast({
        kind: "error",
        message: `Errore salvataggio: ${String(
          (e as { body?: { detail?: string } })?.body?.detail ?? e,
        )}`,
      }),
  });

  // ── Upload manuale ─────────────────────────────────────────────────
  const uploadManual = useMutation({
    mutationFn: (args: { aspect: string; file: File }) =>
      api.uploadManualFlyer(slug ?? "", args.aspect, args.file),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["flyers", slug] });
      setToast({
        kind: "success",
        message: `${res.aspect} caricato (${(res.bytes / 1024).toFixed(0)} KB) ✓`,
      });
    },
    onError: (e) =>
      setToast({
        kind: "error",
        message: `Upload fallito: ${String(
          (e as { body?: { detail?: string } })?.body?.detail ?? e,
        )}`,
      }),
  });

  const deleteFlyer = useMutation({
    mutationFn: (aspect: string) => api.deleteFlyerAspect(slug ?? "", aspect),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["flyers", slug] });
      setToast({
        kind: "success",
        message: `${res.aspect} rimosso (${res.deleted.length} file)`,
      });
    },
  });

  if (!slug) return <div className="text-sm text-slate-500">Slug mancante.</div>;

  const tName = (tournament.data?.input.tournament?.name as string) || slug;
  const status = (tournament.data?.state?.status as string) || "DRAFT";
  const items = flyers.data?.flyers ?? [];
  const hasFlyers = items.length > 0;
  const cacheBust = regen.data || uploadManual.data ? `?t=${Date.now()}` : "";

  // Recupera ultimo step error (se step 3 è fallito)
  const stepsState = tournament.data?.state as { steps?: Record<string, { status?: string; error?: string }> } | undefined;
  const step3Error = stepsState?.steps?.["3"]?.error || "";
  const step3Status = stepsState?.steps?.["3"]?.status || "";

  const generateWithCurrentPrompt = () => {
    runStep.mutate(
      {
        step: 3,
        body: { prompt_override: promptDirty ? promptText : undefined },
      },
      {
        onSuccess: (res) => {
          if (res.status === "ok") {
            setPromptDirty(false);
            setToast({
              kind: "success",
              message: `Locandine generate in ${res.duration_seconds.toFixed(1)}s`,
            });
          } else {
            setToast({
              kind: "error",
              message: `Errore generazione: ${res.error ?? "sconosciuto"}`,
            });
          }
        },
        onError: (e) =>
          setToast({ kind: "error", message: `Errore: ${String(e)}` }),
      },
    );
  };

  const regenerateAll = () => {
    regen.mutate(feedback.trim(), {
      onSuccess: (res) => {
        if (res.status === "ok") {
          setToast({
            kind: "success",
            message: `Rigenerate in ${res.duration_seconds.toFixed(1)}s`,
          });
          setFeedback("");
        } else {
          setToast({
            kind: "error",
            message: `Errore: ${res.error ?? "sconosciuto"}`,
          });
        }
      },
      onError: (e) => setToast({ kind: "error", message: `Errore: ${String(e)}` }),
    });
  };

  const doApprove = () => {
    approve.mutate(undefined, {
      onSuccess: () =>
        setToast({
          kind: "success",
          message: "Locandina approvata — puoi procedere a step 7 (landing)",
        }),
      onError: (e) => setToast({ kind: "error", message: `Errore: ${String(e)}` }),
    });
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Back */}
      <div>
        <Link
          to={`/tournaments/${slug}`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
        >
          <ArrowLeft className="h-4 w-4" />
          {tName}
        </Link>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-slate-900">
            <ImageIcon className="h-6 w-6 text-moving-500" />
            Checkpoint locandina
          </h1>
          <p className="text-sm text-slate-500">
            Carica riferimenti, edita il prompt, genera con NB2 oppure carica
            manualmente. Alla fine approva per passare a step 7.
          </p>
        </div>
        <StatusBadge status={status} />
      </div>

      {/* Errore step 3 (se presente) */}
      {step3Error || step3Status === "failed" ? (
        <Card className="border-red-200">
          <CardBody className="flex items-start gap-3 text-sm text-red-800">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
            <div>
              <div className="font-medium">
                Ultima generazione NB2 fallita
              </div>
              <div className="mt-0.5 text-xs text-red-700">
                {step3Error || "error"}
              </div>
              <div className="mt-1 text-xs text-red-600">
                Suggerimento: rimuovi emoji o frasi problematiche dal prompt
                (sezione più in basso) e riprova. In alternativa carica le
                locandine manualmente.
              </div>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {/* 1 · Reference images */}
      <ReferenceImagesPanel
        slug={slug}
        onToast={(kind, message) => setToast({ kind, message })}
      />

      {/* 2 · Prompt editor */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileEdit className="h-4 w-4" />
              Prompt NB2
              {promptQuery.data?.source === "saved" ? (
                <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] uppercase text-slate-600">
                  salvato
                </span>
              ) : promptQuery.data?.source === "generated" ? (
                <span className="rounded bg-moving-100 px-1.5 py-0.5 text-[10px] uppercase text-moving-700">
                  auto
                </span>
              ) : null}
              {promptDirty ? (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] uppercase text-amber-700">
                  modificato
                </span>
              ) : null}
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPromptOpen((v) => !v)}
            >
              <Eye className="h-3.5 w-3.5" />
              {promptOpen ? "Nascondi" : "Mostra / edita"}
            </Button>
          </div>
        </CardHeader>
        {promptOpen ? (
          <CardBody className="space-y-3">
            <div className="rounded-md border border-court-200 bg-court-50 p-3 text-xs text-court-900">
              <Info className="mr-1 inline h-3.5 w-3.5" />
              Edita il prompt passato a Nano Banana 2. Utile se NB2 blocca
              con <code className="font-mono">NO_IMAGE</code> (content
              filter) — prova a rimuovere emoji, testi complessi o claim
              difficili. Il prompt salvato viene usato al prossimo "Genera".
            </div>
            {promptQuery.isLoading ? (
              <div className="text-xs text-slate-500">Carico prompt…</div>
            ) : (
              <Textarea
                value={promptText}
                onChange={(e) => {
                  setPromptText(e.target.value);
                  setPromptDirty(true);
                }}
                rows={18}
                className="font-mono text-xs"
                placeholder="Il prompt comparirà qui…"
              />
            )}
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-slate-400">
                {promptText.length} caratteri
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (promptQuery.data) {
                      setPromptText(promptQuery.data.prompt);
                      setPromptDirty(false);
                    }
                  }}
                  disabled={!promptDirty}
                >
                  Reset
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => savePrompt.mutate()}
                  loading={savePrompt.isPending}
                  disabled={!promptDirty || promptText.length < 10}
                >
                  <Save className="h-3.5 w-3.5" />
                  Salva prompt
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={generateWithCurrentPrompt}
                  loading={runStep.isPending}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  Genera ora con questo
                </Button>
              </div>
            </div>
          </CardBody>
        ) : null}
      </Card>

      {/* 3 · Locandine attuali + actions */}
      {flyers.isLoading ? (
        <div className="text-sm text-slate-500">Caricamento locandine…</div>
      ) : null}

      {!flyers.isLoading && !hasFlyers ? (
        <Card>
          <CardBody className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-8 w-8 text-amber-500" />
            <div>
              <div className="text-sm font-medium text-slate-700">
                Locandine non ancora generate
              </div>
              <div className="mt-1 text-xs text-slate-500">
                Scegli: genera con NB2 (~60s), oppure carica manualmente qui
                sotto le 3 locandine che hai ricevuto da terzi.
              </div>
            </div>
            <Button
              onClick={generateWithCurrentPrompt}
              loading={runStep.isPending}
            >
              {runStep.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Genera con NB2
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {hasFlyers ? (
        <>
          <Card>
            <CardBody className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-500">
                {items.length} formati disponibili
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={doApprove}
                  loading={approve.isPending}
                >
                  <Check className="h-4 w-4" />
                  Approva locandina
                </Button>
              </div>
            </CardBody>
          </Card>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {items.map((f) => (
              <FlyerPreview
                key={f.filename}
                aspect={f.aspect}
                url={f.url + cacheBust}
                filename={f.filename}
                bytes={f.bytes}
                modifiedAt={f.modified_at}
              />
            ))}
          </div>
        </>
      ) : null}

      {/* 4 · Upload manuale (sempre visibile) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            Carica locandina manualmente
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            Per tornei con locandina fornita da terzi (sponsor, grafico),
            carica i 3 formati qui. Sostituiscono le versioni NB2 senza
            dover passare da "Genera".
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {ASPECTS.map((a) => {
              const existing = items.find((f) => f.aspect === a.id);
              return (
                <div
                  key={a.id}
                  className={cn(
                    "rounded-md border p-3",
                    existing ? "border-moving-200 bg-moving-50/30" : "border-slate-200",
                  )}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-semibold uppercase text-slate-500">
                        {a.label}
                      </div>
                      <div className="text-[10px] text-slate-400">{a.hint}</div>
                    </div>
                    {existing ? (
                      <button
                        className="text-[10px] text-red-600 hover:underline"
                        onClick={() => {
                          if (confirm(`Rimuovere la locandina ${a.id}?`)) {
                            deleteFlyer.mutate(a.id);
                          }
                        }}
                      >
                        <Trash2 className="inline h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                  <label className="flex cursor-pointer items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 bg-white px-3 py-4 text-xs text-slate-600 hover:border-moving-300 hover:text-slate-900">
                    <Upload className="h-3.5 w-3.5" />
                    {uploadManual.isPending &&
                    uploadManual.variables?.aspect === a.id
                      ? "Caricamento…"
                      : existing
                        ? "Sostituisci"
                        : "Carica file"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadManual.mutate({ aspect: a.id, file: f });
                        if (e.target) e.target.value = "";
                      }}
                      disabled={uploadManual.isPending}
                    />
                  </label>
                </div>
              );
            })}
          </div>
        </CardBody>
      </Card>

      {/* 5 · Rigenera con feedback (solo se già ho flyer) */}
      {hasFlyers ? (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Non ti convincono? Rigenerale
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
              Scrivi cosa non ti piace / cosa vorresti. NB2 rigenera tutti e 3
              i formati usando il prompt salvato + il feedback. ~60s.
            </div>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Feedback opzionale per NB2…"
              rows={3}
            />
            <div className="flex justify-end">
              <Button
                variant="outline"
                onClick={regenerateAll}
                loading={regen.isPending}
              >
                {regen.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Rigenera tutte (~60s)
              </Button>
            </div>
          </CardBody>
        </Card>
      ) : null}

      {toast ? (
        <Toast
          kind={toast.kind}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}
